import type { AssetManifest } from '../../shared/assets/manifest-schema';
import {
  type LoaderManifest,
  type LoaderManifestEntry,
  priorityForCategory,
} from '../../shared/assets/loader-types';
import { FEATURES } from '../../shared/config/features';

// ─── ManifestParseError ─────────────────────────────────────────────────────

export class ManifestParseError extends Error {
  constructor(
    public readonly reason:
      | 'missing_schema_version'
      | 'unsupported_schema_version'
      | 'missing_assets'
      | 'malformed_entry',
    public readonly detail: string,
  ) {
    super(`ManifestParseError(${reason}): ${detail}`);
    this.name = 'ManifestParseError';
  }
}

// ─── Per-entry validator ────────────────────────────────────────────────────

/**
 * Validates and normalises a single raw asset entry. Throws
 * `ManifestParseError('malformed_entry', ...)` if any required field
 * is absent or has the wrong type.
 *
 * Required: id, category, path, placeholder, bytes, sourceHash, dracoCompressed.
 * Optional (coerced): placeholderSilence → false, inlineBase64 → false,
 *                     qcStatus → undefined.
 */
function validateEntry(rawEntry: unknown, id: string): LoaderManifestEntry {
  if (rawEntry === null || typeof rawEntry !== 'object') {
    throw new ManifestParseError('malformed_entry', `entry "${id}" is not an object`);
  }

  const e = rawEntry as Record<string, unknown>;

  // Required string fields
  if (typeof e['id'] !== 'string' || e['id'] === '') {
    throw new ManifestParseError('malformed_entry', `entry "${id}" missing required field "id"`);
  }
  if (typeof e['category'] !== 'string' || e['category'] === '') {
    throw new ManifestParseError('malformed_entry', `entry "${id}" missing required field "category"`);
  }
  if (typeof e['path'] !== 'string') {
    throw new ManifestParseError('malformed_entry', `entry "${id}" missing required field "path"`);
  }
  // Sentinel exception: cape-texture-none (and any future no-mesh sentinel) carries
  // path:'' + sourceHash:'none-sentinel' per tools/nanobanana/prompts/capes.ts
  // NO_CAPE_MANIFEST_ENTRY. Empty path is ONLY valid with this exact sourceHash.
  if (e['path'] === '' && e['sourceHash'] !== 'none-sentinel') {
    throw new ManifestParseError(
      'malformed_entry',
      `entry "${id}" has empty path but sourceHash is not 'none-sentinel'`,
    );
  }
  // Required boolean / number fields
  if (typeof e['placeholder'] !== 'boolean') {
    throw new ManifestParseError('malformed_entry', `entry "${id}" missing required field "placeholder"`);
  }

  // sourceHash: must be a string; must be non-empty unless this is a placeholder entry
  if (typeof e['sourceHash'] !== 'string') {
    throw new ManifestParseError('malformed_entry', `entry "${id}" missing required field "sourceHash"`);
  }
  if (!e['placeholder'] && (e['sourceHash'] as string) === '') {
    throw new ManifestParseError(
      'malformed_entry',
      `entry "${id}" has empty sourceHash but placeholder is false`,
    );
  }
  if (typeof e['bytes'] !== 'number') {
    throw new ManifestParseError('malformed_entry', `entry "${id}" missing required field "bytes"`);
  }
  if (typeof e['dracoCompressed'] !== 'boolean') {
    throw new ManifestParseError('malformed_entry', `entry "${id}" missing required field "dracoCompressed"`);
  }

  // Priority — derived from category; will throw if category is not a known value
  const category = e['category'] as AssetManifest['assets'][string]['category'];
  let priority: ReturnType<typeof priorityForCategory>;
  try {
    priority = priorityForCategory(category);
  } catch {
    throw new ManifestParseError(
      'malformed_entry',
      `entry "${id}" has unknown category "${String(e['category'])}"`,
    );
  }

  // Optional fields with coercion
  const placeholderSilence =
    typeof e['placeholderSilence'] === 'boolean' ? e['placeholderSilence'] : false;
  const inlineBase64 =
    typeof e['inlineBase64'] === 'boolean' ? e['inlineBase64'] : false;
  const qcStatus =
    typeof e['qcStatus'] === 'string'
      ? (e['qcStatus'] as LoaderManifestEntry['qcStatus'])
      : undefined;

  return {
    id: e['id'] as string,
    category,
    path: e['path'] as string,
    placeholder: e['placeholder'] as boolean,
    placeholderSilence,
    bytes: e['bytes'] as number,
    dracoCompressed: e['dracoCompressed'] as boolean,
    inlineBase64,
    qcStatus,
    priority,
  };
}

// ─── Minigame category filter ───────────────────────────────────────────────

/**
 * Categories filtered out when FEATURES.MINIGAMES_ENABLED is false.
 *
 * If Plans 25-32 add minigame_sfx, minigame_model, or minigame_texture
 * categories to the manifest schema, those plans MUST add them here too.
 */
const MINIGAME_CATEGORIES: ReadonlySet<string> = new Set(['minigame_icon']);

function isMinigameCategory(category: string): boolean {
  return MINIGAME_CATEGORIES.has(category);
}

// ─── normalizeManifest ──────────────────────────────────────────────────────

/**
 * Validates + normalises a raw AssetManifest (the JSON shape produced by
 * tools/{hy3d,nanobanana,audio}/manifest.ts) into the loader-side LoaderManifest.
 * Pure function — no I/O, no globals.
 *
 * Throws `ManifestParseError` on:
 *   (a) missing `schemaVersion`  → reason: 'missing_schema_version'
 *   (b) schemaVersion ≠ 1        → reason: 'unsupported_schema_version'
 *   (c) missing/non-object `assets` (includes null and arrays)
 *                                → reason: 'missing_assets'
 *   (d) any asset missing a required field
 *                                → reason: 'malformed_entry'
 *
 * Optional field coercion:
 *   - `placeholderSilence` → false when undefined
 *   - `inlineBase64`       → false when undefined
 *   - `qcStatus`           → undefined when missing
 */
export function normalizeManifest(raw: unknown): LoaderManifest {
  if (raw === null || typeof raw !== 'object') {
    throw new ManifestParseError('missing_schema_version', 'manifest is not an object');
  }

  const r = raw as Record<string, unknown>;

  // Validate schemaVersion
  if (!('schemaVersion' in r) || r['schemaVersion'] === undefined || r['schemaVersion'] === null) {
    throw new ManifestParseError('missing_schema_version', 'missing field "schemaVersion"');
  }
  if (r['schemaVersion'] !== 1) {
    throw new ManifestParseError(
      'unsupported_schema_version',
      `expected schemaVersion 1, got ${String(r['schemaVersion'])}`,
    );
  }

  // Validate generatedAt
  const generatedAt =
    typeof r['generatedAt'] === 'string' ? r['generatedAt'] : new Date(0).toISOString();

  // Validate assets — must be a non-null, non-array object
  const rawAssets = r['assets'];
  if (
    rawAssets === null ||
    rawAssets === undefined ||
    typeof rawAssets !== 'object' ||
    Array.isArray(rawAssets)
  ) {
    throw new ManifestParseError(
      'missing_assets',
      'field "assets" must be a non-null, non-array object (Record<string, AssetEntry>)',
    );
  }

  // Validate summary — pass through verbatim (do not recompute)
  // If summary is missing, provide a zero-filled default so the type is satisfied.
  const rawSummary = r['summary'];
  const summary: AssetManifest['summary'] =
    rawSummary !== null &&
    rawSummary !== undefined &&
    typeof rawSummary === 'object' &&
    !Array.isArray(rawSummary)
      ? (rawSummary as AssetManifest['summary'])
      : {
          totalAssets: 0,
          placeholderAssets: 0,
          placeholderRatio: 0,
          cacheHits: 0,
          networkCalls: 0,
        };

  // Normalise each entry
  const assetsRecord = rawAssets as Record<string, unknown>;
  const entries: LoaderManifestEntry[] = [];
  const byId = new Map<string, LoaderManifestEntry>();

  for (const [id, rawEntry] of Object.entries(assetsRecord)) {
    const entry = validateEntry(rawEntry, id);
    if (isMinigameCategory(entry.category) && !FEATURES.MINIGAMES_ENABLED) {
      continue; // skip; filtered out
    }
    entries.push(entry);
    byId.set(entry.id, entry);
  }

  return {
    schemaVersion: 1,
    generatedAt,
    entries,
    byId,
    summary,
  };
}

// ─── loadManifest ────────────────────────────────────────────────────────────

/**
 * Fetches + parses the manifest from a same-origin URL using browser `fetch`.
 *
 * `fetchFn` defaults to `globalThis.fetch`. Callers can inject a fake fetch
 * for tests (the only network interaction in Plan 9 — a same-origin static
 * JSON GET, not an external-API client, so Lesson 23 mock-only prohibition
 * does not apply here).
 *
 * Error handling for failed responses: a `response.ok === false` result
 * throws a plain `Error` (not `ManifestParseError`) because the reason union
 * on `ManifestParseError` covers JSON shape errors, not network/HTTP errors.
 * The plain error message includes the HTTP status code so callers can log it.
 */
export async function loadManifest(
  url: string,
  fetchFn: typeof globalThis.fetch = globalThis.fetch,
): Promise<LoaderManifest> {
  const response = await fetchFn(url, { cache: 'no-store' });

  if (!response.ok) {
    throw new Error(
      `loadManifest: fetch failed with status ${response.status} for URL "${url}"`,
    );
  }

  const rawJson: unknown = await response.json();
  return normalizeManifest(rawJson);
}
